"use client";

import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import { legalDocumentStyles } from "./legalDocumentStyles";
import { PRIVACY_EMAIL } from "@/lib/contact";

export interface LegalDocumentSection {
  id: string;
  eyebrow: string;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
}

interface LegalDocumentShellProps {
  eyebrow: string;
  title: ReactNode;
  description: ReactNode;
  meta?: ReactNode;
  sections: LegalDocumentSection[];
  cta?: ReactNode;
}

export function LegalDocumentShell({
  eyebrow,
  title,
  description,
  meta,
  sections,
  cta,
}: LegalDocumentShellProps) {
  const sectionIds = useMemo(
    () => sections.map((section) => section.id).join("|"),
    [sections],
  );
  const [activeSectionId, setActiveSectionId] = useState(sections[0]?.id ?? "");

  useEffect(() => {
    const ids = sectionIds.split("|").filter(Boolean);
    if (!ids.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visible?.target.id) {
          setActiveSectionId(visible.target.id);
        }
      },
      { rootMargin: "-32% 0px -58% 0px", threshold: [0, 0.2, 0.55, 1] },
    );

    ids.forEach((id) => {
      const node = document.getElementById(id);
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, [sectionIds]);

  return (
    <main className="zmk-legal-root">
      <style>{legalDocumentStyles}</style>
      <div className="zmk-legal-shell">
        <header className="zmk-legal-hero">
          <p className="zmk-legal-kicker">{eyebrow}</p>
          <h1 className="zmk-legal-title">{title}</h1>
          <p className="zmk-legal-description">{description}</p>
          {meta ? <div className="zmk-legal-meta-row">{meta}</div> : null}
        </header>

        <div className="zmk-legal-layout">
          <aside className="zmk-legal-toc" aria-label="Legal page sections">
            <p className="zmk-legal-toc__label">Sections</p>
            <nav className="zmk-legal-toc__list">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className={
                    activeSectionId === section.id
                      ? "zmk-legal-toc__link is-active"
                      : "zmk-legal-toc__link"
                  }
                >
                  {section.eyebrow}
                </a>
              ))}
            </nav>
          </aside>

          <div className="zmk-legal-content">
            {sections.map((section) => (
              <section
                key={section.id}
                id={section.id}
                className="zmk-legal-section"
              >
                <p className="zmk-legal-section__eyebrow">{section.eyebrow}</p>
                <h2 className="zmk-legal-section__title">{section.title}</h2>
                {section.description ? (
                  <div className="zmk-legal-section__description">
                    {section.description}
                  </div>
                ) : null}
                {section.children ? (
                  <div className="zmk-legal-section__body">
                    {section.children}
                  </div>
                ) : null}
              </section>
            ))}

            {cta}
          </div>
        </div>
      </div>
    </main>
  );
}

export function LegalMeta({ children }: { children: ReactNode }) {
  return <span className="zmk-legal-meta">{children}</span>;
}

export function LegalParagraphs({
  paragraphs,
  emphasisFirst = false,
}: {
  paragraphs: readonly string[];
  emphasisFirst?: boolean;
}) {
  return (
    <div className="zmk-legal-prose">
      {paragraphs.map((paragraph, index) => (
        <p
          key={paragraph}
          className={emphasisFirst && index === 0 ? "is-emphasis" : undefined}
        >
          {paragraph}
        </p>
      ))}
    </div>
  );
}

export function LegalBulletList({
  items,
  className = "",
}: {
  items: readonly string[];
  className?: string;
}) {
  return (
    <ul className={`zmk-legal-list ${className}`}>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

export function LegalGrid({
  columns = 2,
  children,
}: {
  columns?: 2 | 3;
  children: ReactNode;
}) {
  return (
    <div
      className={
        columns === 3
          ? "zmk-legal-grid zmk-legal-grid--three"
          : "zmk-legal-grid zmk-legal-grid--two"
      }
    >
      {children}
    </div>
  );
}

export function LegalCard({
  number,
  meta,
  title,
  children,
}: {
  number?: string;
  meta?: string;
  title?: ReactNode;
  children: ReactNode;
}) {
  return (
    <article className="zmk-legal-card">
      {number || meta ? (
        <div className="zmk-legal-card__head">
          {number ? (
            <span className="zmk-legal-card__number">{number}</span>
          ) : (
            <span />
          )}
          {meta ? <span className="zmk-legal-card__meta">{meta}</span> : null}
        </div>
      ) : null}
      {title ? <h3 className="zmk-legal-card__title">{title}</h3> : null}
      <div className="zmk-legal-card__body">{children}</div>
    </article>
  );
}

export function LegalLedger({ children }: { children: ReactNode }) {
  return <div className="zmk-legal-ledger">{children}</div>;
}

export function LegalLedgerRow({
  number,
  title,
  children,
}: {
  number: string;
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <article className="zmk-legal-ledger__row">
      <span className="zmk-legal-ledger__number">{number}</span>
      <div>
        <h3 className="zmk-legal-ledger__title">{title}</h3>
        <div className="zmk-legal-ledger__body">{children}</div>
      </div>
    </article>
  );
}

export function LegalStatGrid({
  stats,
}: {
  stats: readonly { label: string; value: string }[];
}) {
  return (
    <div className="zmk-legal-stat-grid">
      {stats.map((stat) => (
        <div key={stat.label} className="zmk-legal-stat">
          <p className="zmk-legal-stat__value">{stat.value}</p>
          <p className="zmk-legal-stat__label">{stat.label}</p>
        </div>
      ))}
    </div>
  );
}

export function LegalContactCard() {
  return (
    <LegalCard>
      <div className="zmk-legal-contact">
        <div>
          <p className="zmk-legal-card__meta">Email</p>
          <Link className="zmk-legal-link" href={`mailto:${PRIVACY_EMAIL}`}>
            {PRIVACY_EMAIL}
          </Link>
        </div>
        <address>
          Zom Technologies LLC
          <br />
          Attn: Data Privacy Officer
          <br />
          Pennsylvania, United States
        </address>
      </div>
    </LegalCard>
  );
}

export function LegalCtaBand({
  eyebrow,
  title,
  description,
  primary,
  primaryHref,
  secondary,
  secondaryHref,
}: {
  eyebrow: string;
  title: ReactNode;
  description: ReactNode;
  primary: string;
  primaryHref: string;
  secondary?: string;
  secondaryHref?: string;
}) {
  return (
    <section className="zmk-legal-cta">
      <p className="zmk-legal-kicker">{eyebrow}</p>
      <h2 className="zmk-legal-cta__title">{title}</h2>
      <p className="zmk-legal-cta__body">{description}</p>
      <div className="zmk-legal-actions">
        <Link
          className="zmk-legal-button zmk-legal-button--primary"
          href={primaryHref}
        >
          {primary}
          <ArrowUpRight aria-hidden />
        </Link>
        {secondary && secondaryHref ? (
          <Link
            className="zmk-legal-button zmk-legal-button--secondary"
            href={secondaryHref}
          >
            {secondary}
          </Link>
        ) : null}
      </div>
    </section>
  );
}
